import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar as CalendarIcon, Book, Clock, ChevronDown, Calendar, Loader2, Sparkles, Send, RefreshCw, Copy, Download, MapPin, User, X, GraduationCap, BookOpen, Tag } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useToast } from '../../../hooks/useToast';
import {
  createSchedule,
  updateSchedule,
  deleteSchedule,
  shiftScheduleSeries,
  getClassrooms,
} from '../../../hooks/controlEscolarService';
import type { ScheduleInput, Classroom } from '../../../hooks/controlEscolarTypes';

interface AcademicPeriod {
  id: number;
  code: string;
  name: string;
  status: string;
  programId?: number;
  startDate?: string;
  type?: string;
  daySchedules?: Record<string, { startTime: string; endTime: string }>;
}

interface CurriculumCourse {
  id: number;
  code: string;
  name: string;
  credits: number;
}

interface AcademicProgram {
  id: number;
  code: string;
  name: string;
}

interface User {
  id: number;
  fullName: string;
}

const DIAS_SEMANA = [
  { value: 'lunes', label: 'Lunes' },
  { value: 'martes', label: 'Martes' },
  { value: 'miércoles', label: 'Miércoles' },
  { value: 'jueves', label: 'Jueves' },
  { value: 'viernes', label: 'Viernes' },
  { value: 'sábado', label: 'Sábado' },
];

interface ProgramCourseData {
  courseId: number;
  courseCode: string;
  courseName: string;
  courseCredits: number;
  totalSessions: number | null;
  hoursPerSession: string | null;
  equipments: Array<{ id: number; name: string; capacity: number | null }>;
  classrooms: Array<{ id: number; name: string; capacity: number | null; location: string | null }>;
  teachers: Array<{ id: number; fullName: string }>;
  enrolledStudents: number;
  maxCapacity: number | null;
}

export default function PlanificacionCompleta() {
  const { toast } = useToast();
  
  const [periods, setPeriods] = useState<AcademicPeriod[]>([]);
  const [courses, setCourses] = useState<CurriculumCourse[]>([]);
  const [programs, setPrograms] = useState<AcademicProgram[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [professors, setProfessors] = useState<User[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  // Fecha/vista visible del calendario. El calendario se desmonta cada vez que
  // `loading` pasa por true (p.ej. al abrir el modal de edición o al arrastrar
  // un evento), y FullCalendar no recuerda por sí solo dónde estaba navegando
  // al volver a montarse — por eso se persiste aquí y se pasa como
  // initialDate/initialView.
  const [calendarDate, setCalendarDate] = useState<Date | null>(null);
  const [calendarView, setCalendarView] = useState('timeGridWeek');

  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<AcademicPeriod | null>(null);
  const [programCourses, setProgramCourses] = useState<ProgramCourseData[]>([]);

  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any | null>(null);
  const [infoCardEvent, setInfoCardEvent] = useState<{
    id: string;
    title: string;
    classroom: string;
    professor: string;
    program: string;
    period: string;
    periodCode: string;
    day: string;
    startTime: string;
    endTime: string;
    startDate: string;
    endDate: string;
    courseId: number;
    programId: number;
    periodId: number;
    classroomId: number;
    classroomStatus?: string | null;
    professorId: number;
    sessionNumber: number | null;
    totalSessions: number | null;
    needsRescheduling?: boolean;
  } | null>(null);
  const [scheduleFormData, setScheduleFormData] = useState({
    courseId: 0,
    programId: 0,
    periodId: 0,
    dia: 'lunes',
    horaInicio: '',
    horaFin: '',
    classroomId: 0,
    professorId: 0,
    // GQB-05: fecha específica de ESTA sesión al editar (vacío = no tocar la fecha actual).
    fechaEspecifica: '',
  });

  // Filter states
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filterProgramId, setFilterProgramId] = useState<number | null>(null);
  const [filterPeriodId, setFilterPeriodId] = useState<number | null>(null);
  const [filterCourseId, setFilterCourseId] = useState<number | null>(null);
  const [filterClassroomId, setFilterClassroomId] = useState<number | null>(null);
  const [filterProfessorId, setFilterProfessorId] = useState<number | null>(null);
  const [allCalendarEvents, setAllCalendarEvents] = useState<any[]>([]);
  
  // Period search state
  const [periodSearchTerm, setPeriodSearchTerm] = useState('');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  
  // Generate schedules states
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [isStrategyDialogOpen, setIsStrategyDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string>('');
  const [generateSuccess, setGenerateSuccess] = useState<string>('');
  const [generateStep, setGenerateStep] = useState<'info' | 'preview'>('info');
  const [generatePreview, setGeneratePreview] = useState<any[]>([]);
  const [generateWarnings, setGenerateWarnings] = useState<string[]>([]);
  const [generateExpectedTotal, setGenerateExpectedTotal] = useState<number>(0);
  
  // Delete schedules states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasConflicts, setHasConflicts] = useState(false);
  const [conflictCount, setConflictCount] = useState(0);
  const [selectedStrategy, setSelectedStrategy] = useState<'replace' | 'append'>('replace');

  // AI Generation states
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [aiStep, setAIStep] = useState<'rules' | 'generating' | 'preview' | 'refining'>('rules');
  const [aiRules, setAIRules] = useState('');
  const [aiSessionId, setAISessionId] = useState<number | null>(null);
  const [aiPreviewSchedules, setAIPreviewSchedules] = useState<any[]>([]);
  const [aiExplanation, setAIExplanation] = useState('');
  const [aiFeedback, setAIFeedback] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiWarnings, setAIWarnings] = useState<string[]>([]);
  const [aiError, setAIError] = useState('');
  const [aiProgressMessage, setAIProgressMessage] = useState('');
  const [aiExpectedTotal, setAIExpectedTotal] = useState<number>(0);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedPeriod.programId) {
      loadProgramData(selectedPeriod.programId, selectedPeriod.id);
    }
  }, [selectedPeriod]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Cargar programas académicos, períodos/grupos y todos los horarios al inicio
      const [programsRes, periodsRes, calendarRes] = await Promise.all([
        fetch('/api/academic-programs', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/academic-periods', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/schedules/calendar-view', { credentials: 'include' }).then(r => r.json()),
      ]);

      if (Array.isArray(programsRes)) {
        setPrograms(programsRes.sort((a: any, b: any) => a.name.localeCompare(b.name, 'es')));
      }

      const validStatuses = ['active', 'promocion', 'activo'];
      setPeriods(
        periodsRes
          .filter((p: any) => validStatuses.includes(p.status))
          .sort((a: any, b: any) => a.name.localeCompare(b.name, 'es'))
      );

      // Cargar todos los eventos del calendario sin filtros
      if (calendarRes && (calendarRes as any).events) {
        setAllCalendarEvents((calendarRes as any).events);
        setCalendarEvents((calendarRes as any).events);
      }
    } catch (error) {
      console.error('Error loading periods:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los períodos/grupos académicos',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProgramData = async (programId: number, periodId?: number) => {
    try {
      setLoading(true);
      // Cargar todos los datos cuando se selecciona un programa
      const schedulePlanningUrl = periodId 
        ? `/api/academic-programs/${programId}/schedule-planning?periodId=${periodId}`
        : `/api/academic-programs/${programId}/schedule-planning`;
      
      const [periodsRes, coursesRes, classroomsRes, professorsRes, calendarRes, programCoursesRes] = await Promise.all([
        fetch('/api/academic-periods', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/curriculum-courses', { credentials: 'include' }).then(r => r.json()),
        getClassrooms(),
        fetch('/api/users/professors', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/schedules/calendar-view', { credentials: 'include' }).then(r => r.json()),
        fetch(schedulePlanningUrl, { credentials: 'include' }).then(r => r.json()),
      ]);

      const validStatuses = ['active', 'promocion', 'activo'];
      setPeriods(
        periodsRes
          .filter((p: any) => validStatuses.includes(p.status))
          .sort((a: any, b: any) => a.name.localeCompare(b.name, 'es'))
      );
      setCourses(coursesRes);
      setClassrooms(classroomsRes.filter((c: Classroom) => c.status === 'activa'));
      setProfessors(professorsRes);
      setProgramCourses(programCoursesRes.courses || []);
      
      if (calendarRes && (calendarRes as any).events) {
        setAllCalendarEvents((calendarRes as any).events);
        setCalendarEvents((calendarRes as any).events);
      }
    } catch (error) {
      console.error('Error loading program data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los datos del programa',
      });
    } finally {
      setLoading(false);
    }
  };


  const refreshCalendar = async () => {
    try {
      const calendarRes = await fetch(`/api/schedules/calendar-view?_t=${Date.now()}`, { credentials: 'include', cache: 'no-store' }).then(r => r.json());
      if (calendarRes && (calendarRes as any).events) {
        const events = (calendarRes as any).events;
        setAllCalendarEvents(events);

        let filtered = events;
        if (filterProgramId) filtered = filtered.filter((e: any) => e.extendedProps?.programId === filterProgramId);
        if (filterPeriodId) filtered = filtered.filter((e: any) => e.extendedProps?.periodId === filterPeriodId);
        if (filterCourseId) filtered = filtered.filter((e: any) => e.extendedProps?.courseId === filterCourseId);
        if (filterClassroomId) filtered = filtered.filter((e: any) => e.extendedProps?.classroomId === filterClassroomId);
        if (filterProfessorId) filtered = filtered.filter((e: any) => e.extendedProps?.professorId === filterProfessorId);
        setCalendarEvents(filtered);
      }
    } catch (error) {
      console.error('Error refreshing calendar:', error);
    }
  };

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const scheduleData: ScheduleInput = {
        courseId: scheduleFormData.courseId,
        programId: scheduleFormData.programId,
        periodId: scheduleFormData.periodId,
        dia: scheduleFormData.dia,
        horaInicio: scheduleFormData.horaInicio,
        horaFin: scheduleFormData.horaFin,
        classroomId: scheduleFormData.classroomId || undefined,
        professorId: scheduleFormData.professorId || undefined,
      };

      await createSchedule(scheduleData);

      toast({
        title: 'Éxito',
        description: 'Horario creado correctamente',
      });

      setIsScheduleDialogOpen(false);
      resetScheduleForm();
      await refreshCalendar();
    } catch (error: any) {
      const errorData = error.response?.data || {};
      const conflicts = errorData.conflicts || [];
      const warnings = errorData.warnings || [];
      
      let errorMessage = error.message || 'No se pudo crear el horario';
      
      if (conflicts.length > 0) {
        errorMessage = 'Conflictos detectados:\n' + conflicts.map((c: string) => `• ${c}`).join('\n');
      }
      
      if (warnings.length > 0) {
        errorMessage += '\n\nAdvertencias:\n' + warnings.map((w: string) => `• ${w}`).join('\n');
      }
      
      toast({
        variant: 'destructive',
        title: 'Error al crear horario',
        description: errorMessage,
      });
    }
  };

  const handleUpdateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSchedule) return;

    // GQB-05: si esta clase hoy es recurrente (sin fecha propia — se repite el mismo día de
    // la semana indefinidamente) y el usuario le está asignando una fecha específica, esa
    // fila deja de representar la serie completa y pasa a ser una sola sesión en esa fecha —
    // las demás semanas de esta clase recurrente dejan de aparecer en el calendario. Pedir
    // confirmación explícita en vez de que se pierdan en silencio (visto reproduciendo el bug
    // original: arrastrar una clase recurrente a otra semana borraba las demás sin avisar).
    const wasRecurring = !editingSchedule.startDate;
    const willGetDate = !!scheduleFormData.fechaEspecifica;
    if (wasRecurring && willGetDate) {
      const confirmed = window.confirm(
        'Esta clase se repite cada semana (no tiene una fecha fija). Si le pones una fecha específica, ' +
        'esta fila pasará a ser una sola sesión en esa fecha y las demás semanas de esta clase ya no ' +
        'aparecerán en el calendario.\n\n¿Quieres continuar de todas formas?'
      );
      if (!confirmed) return;
    }

    try {
      const scheduleData: ScheduleInput = {
        courseId: scheduleFormData.courseId,
        programId: scheduleFormData.programId,
        periodId: scheduleFormData.periodId,
        dia: scheduleFormData.dia,
        horaInicio: scheduleFormData.horaInicio,
        horaFin: scheduleFormData.horaFin,
        classroomId: scheduleFormData.classroomId || undefined,
        professorId: scheduleFormData.professorId || undefined,
        startDate: scheduleFormData.fechaEspecifica || editingSchedule.startDate || undefined,
        endDate: scheduleFormData.fechaEspecifica || editingSchedule.endDate || undefined,
      };

      await updateSchedule(editingSchedule.id, scheduleData);

      toast({
        title: 'Éxito',
        description: 'Horario actualizado correctamente',
      });

      setIsScheduleDialogOpen(false);
      resetScheduleForm();
      await refreshCalendar();
    } catch (error: any) {
      const errorData = error.response?.data || {};
      const conflicts = errorData.conflicts || [];
      const warnings = errorData.warnings || [];
      
      let errorMessage = error.message || 'No se pudo actualizar el horario';
      
      if (conflicts.length > 0) {
        errorMessage = 'Conflictos detectados:\n' + conflicts.map((c: string) => `• ${c}`).join('\n');
      }
      
      if (warnings.length > 0) {
        errorMessage += '\n\nAdvertencias:\n' + warnings.map((w: string) => `• ${w}`).join('\n');
      }
      
      toast({
        variant: 'destructive',
        title: 'Error al actualizar horario',
        description: errorMessage,
      });
    }
  };

  const handleShiftSeries = async () => {
    if (!editingSchedule?.startDate) return;
    const ok = window.confirm(
      'Se recorrerá esta clase y todas las siguientes de esta materia una sesión hacia adelante ' +
      '(la última cae en la siguiente fecha disponible y puede extender la fecha de término del grupo). ¿Continuar?'
    );
    if (!ok) return;
    try {
      const result = await shiftScheduleSeries(editingSchedule.id);
      const last = result.moves[result.moves.length - 1];
      toast({
        title: 'Calendarización recorrida',
        description:
          `${result.moves.length} sesión(es) movidas; la última queda el ${last.toDate}.` +
          (result.warnings.length ? '\nAvisos:\n' + result.warnings.map(w => `• ${w}`).join('\n') : ''),
      });
      setIsScheduleDialogOpen(false);
      resetScheduleForm();
      await refreshCalendar();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'No se pudo recorrer', description: error.message });
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!confirm('¿Estás seguro de eliminar este horario?')) return;

    try {
      await deleteSchedule(scheduleId);
      toast({
        title: 'Éxito',
        description: 'Horario eliminado correctamente',
      });
      await refreshCalendar();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'No se pudo eliminar el horario',
      });
    }
  };

  const resetScheduleForm = () => {
    setEditingSchedule(null);
    setScheduleFormData({
      courseId: 0,
      programId: selectedPeriod?.programId || 0,
      periodId: 0,
      dia: 'lunes',
      horaInicio: '',
      horaFin: '',
      classroomId: 0,
      professorId: 0,
      fechaEspecifica: '',
    });
  };

  const getAvailableDays = () => {
    const period = scheduleFormData.periodId 
      ? periods.find(p => p.id === scheduleFormData.periodId)
      : selectedPeriod;
    
    if (!period || !period.daySchedules) return [];
    
    const daySchedules = period.daySchedules as Record<string, any>;
    const reverseMapping: Record<string, string> = {
      'Monday': 'lunes',
      'Tuesday': 'martes',
      'Wednesday': 'miércoles',
      'Thursday': 'jueves',
      'Friday': 'viernes',
      'Saturday': 'sábado',
      'Sunday': 'domingo',
    };
    
    // Filtrar solo los días que están en daySchedules
    const availableDays = DIAS_SEMANA.filter(day => {
      const mappedDay = reverseMapping[day.label.charAt(0).toUpperCase() + day.label.slice(1)];
      const englishDay = Object.entries(reverseMapping).find(([, es]) => es === day.value)?.[0];
      return englishDay && Object.keys(daySchedules).includes(englishDay);
    });
    
    return availableDays;
  };

  const getPermittedTimeRange = () => {
    const period = scheduleFormData.periodId 
      ? periods.find(p => p.id === scheduleFormData.periodId)
      : selectedPeriod;
    
    if (!period || !period.daySchedules) return null;
    
    const dayMapping: Record<string, string> = {
      'lunes': 'Monday',
      'martes': 'Tuesday',
      'miércoles': 'Wednesday',
      'jueves': 'Thursday',
      'viernes': 'Friday',
      'sábado': 'Saturday',
      'domingo': 'Sunday',
    };
    
    const mappedDay = dayMapping[scheduleFormData.dia.toLowerCase()];
    return period.daySchedules[mappedDay] || null;
  };

  const formatPeriodSchedule = () => {
    const period = selectedPeriod;
    if (!period || !period.daySchedules) return [];
    
    const daySchedules = period.daySchedules as Record<string, any>;
    const dayLabels: Record<string, string> = {
      'Monday': 'Lunes',
      'Tuesday': 'Martes',
      'Wednesday': 'Miércoles',
      'Thursday': 'Jueves',
      'Friday': 'Viernes',
      'Saturday': 'Sábado',
      'Sunday': 'Domingo',
    };
    
    const scheduleItems: string[] = [];
    Object.entries(daySchedules).forEach(([englishDay, times]) => {
      const label = dayLabels[englishDay] || englishDay;
      if (times && (times as any).timeSlots && Array.isArray((times as any).timeSlots)) {
        (times as any).timeSlots.forEach((slot: any, idx: number) => {
          if (slot.startTime && slot.endTime) {
            scheduleItems.push(`${label} Bloque ${idx + 1}: ${slot.startTime} - ${slot.endTime}`);
          }
        });
      } else if (times && (times as any).startTime && (times as any).endTime) {
        scheduleItems.push(`${label}: ${(times as any).startTime} - ${(times as any).endTime}`);
      }
    });
    
    return scheduleItems;
  };

  // Get day and schedule for a specific course from daySchedules
  const getCourseSchedule = (courseId: number): { day: string; dayEnglish: string; startTime: string; endTime: string; dayOrder: number } | null => {
    if (!selectedPeriod || !selectedPeriod.daySchedules) return null;
    
    const daySchedules = selectedPeriod.daySchedules as Record<string, any>;
    const dayLabels: Record<string, string> = {
      'Monday': 'Lunes',
      'Tuesday': 'Martes',
      'Wednesday': 'Miércoles',
      'Thursday': 'Jueves',
      'Friday': 'Viernes',
      'Saturday': 'Sábado',
      'Sunday': 'Domingo',
    };
    
    const dayOrder: Record<string, number> = {
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6,
      'Sunday': 7,
    };
    
    for (const [englishDay, dayConfig] of Object.entries(daySchedules)) {
      if (dayConfig?.timeSlots && Array.isArray(dayConfig.timeSlots)) {
        for (const slot of dayConfig.timeSlots) {
          if (slot.courses && Array.isArray(slot.courses)) {
            const foundCourse = slot.courses.find((c: any) => c.courseId === courseId);
            if (foundCourse) {
              return {
                day: dayLabels[englishDay] || englishDay,
                dayEnglish: englishDay,
                startTime: slot.startTime || '',
                endTime: slot.endTime || '',
                dayOrder: dayOrder[englishDay] || 7
              };
            }
          }
        }
      }
      if (dayConfig?.courses && Array.isArray(dayConfig.courses)) {
        const foundCourse = dayConfig.courses.find((c: any) => c.courseId === courseId);
        if (foundCourse) {
          return {
            day: dayLabels[englishDay] || englishDay,
            dayEnglish: englishDay,
            startTime: dayConfig.startTime || '',
            endTime: dayConfig.endTime || '',
            dayOrder: dayOrder[englishDay] || 7
          };
        }
      }
    }
    return null;
  };

  // Sort programCourses by day of week and start time
  const getSortedCourses = () => {
    const coursesWithSchedule = programCourses.map(course => {
      const baseSchedule = getCourseSchedule(course.courseId);
      
      // Extract startDate and endDate from calendar events for this course
      const courseEvents = calendarEvents.filter(e => e.extendedProps?.courseId === course.courseId);
      let startDate = undefined;
      let endDate = undefined;
      
      if (courseEvents.length > 0) {
        // Sort events by date to get first and last
        const sortedEvents = courseEvents.sort((a, b) => {
          const dateA = a.extendedProps?.startDate || '';
          const dateB = b.extendedProps?.startDate || '';
          return dateA.localeCompare(dateB);
        });
        startDate = sortedEvents[0]?.extendedProps?.startDate;
        endDate = sortedEvents[sortedEvents.length - 1]?.extendedProps?.endDate;
      }
      
      return {
        course,
        schedule: baseSchedule ? { ...baseSchedule, startDate, endDate } : null
      };
    });
    
    return coursesWithSchedule.sort((a, b) => {
      if (!a.schedule || !b.schedule) return 0;
      
      // First sort by day order (Monday first, then Tuesday, etc)
      if (a.schedule.dayOrder !== b.schedule.dayOrder) {
        return a.schedule.dayOrder - b.schedule.dayOrder;
      }
      
      // Then sort by start time
      return a.schedule.startTime.localeCompare(b.schedule.startTime);
    }).map(item => ({ course: item.course, schedule: item.schedule }));
  };

  const openNewSchedule = () => {
    resetScheduleForm();
    // Establecer el primer día disponible del período
    const availableDays = getAvailableDays();
    if (availableDays.length > 0) {
      setScheduleFormData(prev => ({
        ...prev,
        dia: availableDays[0].value
      }));
    }
    setIsScheduleDialogOpen(true);
  };

  const openEditSchedule = async (scheduleId: number, extendedProps: any) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/schedules/${scheduleId}`, { 
        credentials: 'include' 
      });
      
      if (!response.ok) {
        throw new Error('No se pudo cargar el horario');
      }
      
      const schedule = await response.json();
      
      const matchingPeriod = periods.find(p => p.programId === schedule.programId);
      if (matchingPeriod) {
        setSelectedPeriod(matchingPeriod);
      }

      const effectivePeriodId = schedule.periodId || matchingPeriod?.id;
      const schedulePlanningUrl = effectivePeriodId
        ? `/api/academic-programs/${schedule.programId}/schedule-planning?periodId=${effectivePeriodId}`
        : `/api/academic-programs/${schedule.programId}/schedule-planning`;

      const [classroomsRes, professorsRes, programCoursesRes] = await Promise.all([
        getClassrooms(),
        fetch('/api/users/professors', { credentials: 'include' }).then(r => r.json()),
        fetch(schedulePlanningUrl, { credentials: 'include' }).then(r => r.json()),
      ]);

      setClassrooms(classroomsRes.filter((c: Classroom) => c.status === 'activa'));
      setProfessors(professorsRes);
      setProgramCourses(programCoursesRes.courses || []);
      
      setEditingSchedule(schedule);
      setScheduleFormData({
        courseId: schedule.courseId,
        programId: schedule.programId,
        periodId: schedule.periodId || effectivePeriodId || 0,
        dia: schedule.dia,
        horaInicio: schedule.horaInicio,
        horaFin: schedule.horaFin,
        classroomId: schedule.classroomId || 0,
        professorId: schedule.professorId || 0,
        fechaEspecifica: schedule.startDate || '',
      });
      
      setIsScheduleDialogOpen(true);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'No se pudo cargar el horario',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter function
  const filterAndDisplayEvents = () => {
    let filtered = allCalendarEvents;
    
    if (filterProgramId) {
      filtered = filtered.filter(e => e.extendedProps?.programId === filterProgramId);
    }
    if (filterPeriodId) {
      filtered = filtered.filter(e => e.extendedProps?.periodId === filterPeriodId);
    }
    if (filterCourseId) {
      filtered = filtered.filter(e => e.extendedProps?.courseId === filterCourseId);
    }
    if (filterClassroomId) {
      filtered = filtered.filter(e => e.extendedProps?.classroomId === filterClassroomId);
    }
    if (filterProfessorId) {
      filtered = filtered.filter(e => e.extendedProps?.professorId === filterProfessorId);
    }
    
    setCalendarEvents(filtered);
  };

  // Apply filters when filter states change
  useEffect(() => {
    filterAndDisplayEvents();
  }, [filterProgramId, filterPeriodId, filterCourseId, filterClassroomId, filterProfessorId]);

  const resetFilters = () => {
    setFilterProgramId(null);
    setFilterPeriodId(null);
    setFilterCourseId(null);
    setFilterClassroomId(null);
    setFilterProfessorId(null);
    setCalendarEvents(allCalendarEvents);
  };

  const checkForConflicts = async () => {
    if (!selectedPeriod) {
      setGenerateError('Debe seleccionar un período');
      return;
    }

    try {
      const response = await fetch(`/api/schedules/check-conflicts/${selectedPeriod.programId}/${selectedPeriod.id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al verificar conflictos');
      }

      const data = await response.json();
      setHasConflicts(data.hasConflicts);
      setConflictCount(data.conflictCount);

      if (data.hasConflicts) {
        setIsStrategyDialogOpen(true);
      } else {
        setSelectedStrategy('replace');
        await handleGeneratePreview('replace');
      }
    } catch (err: any) {
      console.error('Error checking conflicts:', err);
      setGenerateError('Error al verificar conflictos');
    }
  };

  const handleGeneratePreview = async (strategy: 'replace' | 'append' = selectedStrategy) => {
    if (!selectedPeriod) {
      setGenerateError('Debe seleccionar un período');
      return;
    }

    setIsGenerating(true);
    setGenerateError('');
    setGenerateSuccess('');
    setIsStrategyDialogOpen(false);

    try {
      const response = await fetch('/api/schedules/generate-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          programId: selectedPeriod.programId,
          periodId: selectedPeriod.id,
          strategy,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al generar vista previa');
      }

      const data = await response.json();
      setGeneratePreview(data.preview || []);
      setGenerateWarnings(data.advertencias || []);
      setGenerateExpectedTotal(data.estadisticas?.expectedTotal || 0);
      setGenerateStep('preview');
    } catch (err: any) {
      console.error('Error generando vista previa:', err);
      setGenerateError(err.message || 'Error al generar vista previa');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyPreview = async () => {
    if (!selectedPeriod || generatePreview.length === 0) return;

    setIsGenerating(true);
    setGenerateError('');

    try {
      const response = await fetch('/api/schedules/apply-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          programId: selectedPeriod.programId,
          periodId: selectedPeriod.id,
          preview: generatePreview,
          strategy: selectedStrategy,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al aplicar horarios');
      }

      const data = await response.json();
      if (data.failed > 0) {
        const motivos = (data.errors || [])
          .map((e: any) => `${e.schedule?.materia || e.schedule?.materiaId}: ${e.error}`)
          .join('\n');
        setGenerateSuccess(`Se aplicaron ${data.count} horarios. ${data.failed} sesión(es) NO se aplicaron:\n${motivos}`);
      } else {
        setGenerateSuccess(`Se aplicaron ${data.count} horarios exitosamente`);
      }

      setTimeout(() => {
        loadProgramData(selectedPeriod.programId || 0, selectedPeriod.id);
        setIsGenerateDialogOpen(false);
        setGenerateStep('info');
        setGeneratePreview([]);
        setHasConflicts(false);
        setConflictCount(0);
        window.dispatchEvent(new CustomEvent('schedulesChanged', { detail: { periodId: selectedPeriod.id } }));
      }, 1500);
    } catch (err: any) {
      console.error('Error aplicando horarios:', err);
      setGenerateError(err.message || 'Error al aplicar horarios');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyPreviewToClipboard = (preview: any[]) => {
    const text = preview.map((s: any) => 
      `${s.fecha}\t${s.dia}\t${s.horaInicio}-${s.horaFin}\t${s.materia}\t${s.profesor}\t${s.aula}`
    ).join('\n');
    navigator.clipboard.writeText(`Fecha\tDía\tHorario\tMateria\tProfesor\tAula\n${text}`);
    toast({ title: 'Copiado', description: 'Vista previa copiada al portapapeles' });
  };

  const downloadPreviewCSV = (preview: any[], filename: string) => {
    // GQB-07: 'Clase' y 'Reprogramar' replican exactamente lo que ya se ve en la tabla de
    // vista previa (líneas ~2390-2398) — antes el CSV se generaba aparte y se le olvidaban.
    const headers = ['Fecha', 'Día', 'Hora Inicio', 'Hora Fin', 'Materia', 'Profesor', 'Aula', 'Clase', 'Reprogramar'];
    const rows = preview.map((s: any) => [
      s.fecha,
      s.dia,
      s.horaInicio,
      s.horaFin,
      s.materia,
      s.profesor,
      s.aula,
      s.sessionNumber && s.totalSessions ? `${s.sessionNumber} de ${s.totalSessions}` : '',
      s.needsRescheduling ? 'Sí' : '',
    ]);
    // Comillas en cada campo (mismo patrón que el CSV del flujo con IA, líneas ~2703-2706)
    // para que una materia/profesor/aula con coma no corrompa las columnas al abrir en Excel.
    const csv = [headers, ...rows]
      .map(row => row.map((cell) => `"${cell ?? ''}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeletePeriodSchedules = async () => {
    if (!selectedPeriod) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/schedules/period/${selectedPeriod.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al eliminar horarios');
      }

      const data = await response.json();
      setGenerateSuccess(`Se eliminaron ${data.deletedCount} horarios del período`);
      setIsDeleteDialogOpen(false);
      
      refreshCalendar();
      // Notify other components that schedules have changed
      window.dispatchEvent(new CustomEvent('schedulesChanged', { detail: { periodId: selectedPeriod.id } }));
    } catch (err: any) {
      console.error('Error eliminando horarios:', err);
      setGenerateError(err.message || 'Error al eliminar horarios');
    } finally {
      setIsDeleting(false);
    }
  };

  const openAIDialog = async () => {
    if (!selectedPeriod) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Debe seleccionar un período primero',
      });
      return;
    }

    setAIError('');
    setAIStep('rules');
    setAIPreviewSchedules([]);
    setAIExplanation('');
    setAISessionId(null);
    setAIFeedback('');
    setAIWarnings([]);
    setIsAIDialogOpen(true);

    try {
      const response = await fetch('/api/schedule-rules', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAIRules(data.rules || '');
      }
    } catch (error) {
      console.error('Error loading rules:', error);
    }
  };

  const handleAIGenerate = async () => {
    if (!selectedPeriod) return;

    setAILoading(true);
    setAIError('');
    setAIPreviewSchedules([]);
    setAIExplanation('');
    setAIWarnings([]);
    setAIProgressMessage('Guardando reglas...');
    setAIStep('generating');

    try {
      const saveResponse = await fetch('/api/schedule-rules', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rules: aiRules }),
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({}));
        setAILoading(false);
        setAIStep('rules');
        throw new Error(errorData.error || 'Error al guardar las reglas. Intente nuevamente.');
      }

      const savedData = await saveResponse.json();
      setAIRules(savedData.rules);

      setAIProgressMessage('Iniciando generacion con IA...');

      const eventSource = new EventSource(`/api/ai-schedule/generate-stream/${selectedPeriod.id}`);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'progress') {
            setAIProgressMessage(data.message);
          } else if (data.type === 'complete') {
            eventSource.close();
            setAISessionId(data.sessionId);
            setAIPreviewSchedules(data.schedules || []);
            setAIExplanation(data.explanation || '');
            setAIWarnings(data.advertencias || []);
            setAIExpectedTotal(data.expectedTotal || 0);
            setAIProgressMessage('');
            setAIStep('preview');
            setAILoading(false);
          } else if (data.type === 'error') {
            eventSource.close();
            setAIError(data.error || 'Error al generar horarios');
            setAIProgressMessage('');
            setAIStep('rules');
            setAILoading(false);
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE Error:', error);
        eventSource.close();
        setAIError('Error de conexion con el servidor');
        setAIProgressMessage('');
        setAIStep('rules');
        setAILoading(false);
      };

    } catch (err: any) {
      console.error('Error in AI generation:', err);
      setAIError(err.message || 'Error al generar horarios con IA');
      setAIProgressMessage('');
      setAIStep('rules');
      setAILoading(false);
    }
  };

  const handleAIRefine = async () => {
    if (!aiSessionId || !aiFeedback.trim()) return;

    setAILoading(true);
    setAIError('');
    setAIProgressMessage('Procesando ajustes...');
    setAIStep('refining');

    try {
      const eventSource = new EventSource(
        `/api/ai-schedule/refine-stream/${aiSessionId}?feedback=${encodeURIComponent(aiFeedback)}`
      );

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'progress') {
            setAIProgressMessage(data.message);
          } else if (data.type === 'complete') {
            eventSource.close();
            setAIPreviewSchedules(data.schedules || []);
            setAIExplanation(data.explanation || '');
            setAIFeedback('');
            setAIProgressMessage('');
            setAIStep('preview');
            setAILoading(false);
          } else if (data.type === 'error') {
            eventSource.close();
            setAIError(data.error || 'Error al refinar horarios');
            setAIProgressMessage('');
            setAIStep('preview');
            setAILoading(false);
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE Error:', error);
        eventSource.close();
        setAIError('Error de conexion con el servidor');
        setAIProgressMessage('');
        setAIStep('preview');
        setAILoading(false);
      };

    } catch (err: any) {
      console.error('Error refining:', err);
      setAIError(err.message || 'Error al refinar horarios');
      setAIProgressMessage('');
      setAIStep('preview');
      setAILoading(false);
    }
  };

  const handleAIApply = async () => {
    if (!aiSessionId || !selectedPeriod) return;

    setAILoading(true);
    setAIError('');

    try {
      const response = await fetch('/api/ai-schedule/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: aiSessionId,
          strategy: selectedStrategy,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al aplicar horarios');
      }

      const data = await response.json();
      toast({
        title: 'Horarios aplicados',
        description: data.message || `${data.count} clases programadas exitosamente`,
      });

      setIsAIDialogOpen(false);
      loadProgramData(selectedPeriod.programId || 0);
      // Notify other components that schedules have changed
      window.dispatchEvent(new CustomEvent('schedulesChanged', { detail: { periodId: selectedPeriod.id } }));
    } catch (err: any) {
      console.error('Error applying:', err);
      setAIError(err.message || 'Error al aplicar horarios');
    } finally {
      setAILoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Planificación de Horarios por Programa</h1>
        <p className="text-gray-600">Gestiona la planificación académica y visualiza horarios en calendario</p>
      </div>

      <Tabs defaultValue="planning" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="planning">Planificación</TabsTrigger>
          <TabsTrigger value="calendar">Calendario / Horarios</TabsTrigger>
        </TabsList>

        <TabsContent value="planning">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-6">
              {/* Period/Group Selection */}
              {(
                <div>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                  <Label>Períodos / Grupos Académicos:</Label>
                  <div className="relative">
                    <input
                      type="text"
                      value={isPeriodDropdownOpen ? periodSearchTerm : (selectedPeriod ? `${selectedPeriod.code} - ${selectedPeriod.name}` : '')}
                      onChange={(e) => {
                        setPeriodSearchTerm(e.target.value);
                        setIsPeriodDropdownOpen(true);
                      }}
                      onFocus={() => {
                        setIsPeriodDropdownOpen(true);
                        setPeriodSearchTerm('');
                      }}
                      onBlur={() => {
                        setTimeout(() => setIsPeriodDropdownOpen(false), 200);
                      }}
                      placeholder="Buscar período/grupo..."
                      className="w-64 px-3 py-2 border border-gray-300 rounded-md"
                    />
                    {isPeriodDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-80 max-h-60 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg">
                        {periods
                          .filter(p => 
                            periodSearchTerm === '' ||
                            p.code.toLowerCase().includes(periodSearchTerm.toLowerCase()) ||
                            p.name.toLowerCase().includes(periodSearchTerm.toLowerCase())
                          )
                          .map(period => (
                            <div
                              key={period.id}
                              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedPeriod?.id === period.id ? 'bg-blue-100' : ''}`}
                              onMouseDown={() => {
                                setSelectedPeriod(period);
                                setPeriodSearchTerm('');
                                setIsPeriodDropdownOpen(false);
                              }}
                            >
                              <span className="font-medium">{period.code}</span> - {period.name}
                            </div>
                          ))}
                        {periods.filter(p => 
                          periodSearchTerm === '' ||
                          p.code.toLowerCase().includes(periodSearchTerm.toLowerCase()) ||
                          p.name.toLowerCase().includes(periodSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="px-3 py-2 text-gray-500">No se encontraron resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedPeriod && selectedPeriod.startDate && (
                    <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-md">
                      <span className="text-sm font-medium text-blue-900">
                        Fecha de Inicio: <span className="font-semibold">{selectedPeriod.startDate}</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setGenerateError('');
                      setGenerateSuccess('');
                      setIsGenerateDialogOpen(true);
                    }}
                    disabled={!selectedPeriod}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Generar Horario
                  </Button>
                  <Button
                    onClick={openAIDialog}
                    disabled={!selectedPeriod}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generar con IA
                  </Button>
                  <Button
                    onClick={() => setIsDeleteDialogOpen(true)}
                    disabled={!selectedPeriod || isDeleting}
                    variant="destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar Horarios
                  </Button>
                </div>
              </div>

              {loading ? (
                <p className="text-gray-500 text-center py-8">Cargando...</p>
              ) : !selectedPeriod ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Selecciona un período/grupo académico para ver sus materias</p>
                </div>
              ) : programCourses.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Book className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No hay materias asignadas al programa de este período/grupo</p>
                </div>
              ) : (
                <div className="w-full">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Materia
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Día
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Horario
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          N° Clases
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Hrs/Clase
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Aula
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Equipamiento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Alumnos Inscritos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Capacidad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Profesores
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedCourses().map(({ course, schedule }) => {
                        return (
                        <tr key={course.courseId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="font-medium">{course.courseName}</div>
                            <div className="text-gray-500 text-xs">{course.courseCode}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {schedule?.day || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {schedule ? `${schedule.startTime} - ${schedule.endTime}` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                            {course.totalSessions || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                            {course.hoursPerSession || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {course.classrooms.length > 0 ? (
                              <div className="space-y-1">
                                {course.classrooms.map(classroom => (
                                  <div key={classroom.id} className="text-xs">
                                    {classroom.name}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">Sin aulas disponibles</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {course.equipments.length > 0 ? (
                              <div className="space-y-1">
                                {course.equipments.map(equipment => (
                                  <div key={equipment.id} className="text-xs">
                                    {equipment.name}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">Sin requisitos</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {course.enrolledStudents}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {course.maxCapacity || 'Sin límite'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {course.teachers.length > 0 ? (
                              <div className="space-y-1">
                                {course.teachers.map(teacher => (
                                  <div key={teacher.id} className="text-xs">
                                    {teacher.fullName}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">Sin profesores asignados</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="calendar">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Calendario de Horarios</h2>
                <p className="text-sm text-gray-600 mt-1">Visualiza y organiza los horarios de clase</p>
              </div>
              <Button onClick={openNewSchedule}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Horario
              </Button>
            </div>

            {/* Filters Section */}
            <div className="mb-6 bg-gray-50 rounded-lg border border-gray-200">
              <button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-100 transition-colors"
              >
                <span className="font-semibold text-gray-900">🔍 Filtros</span>
                <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
              </button>

              {isFiltersOpen && (
                <div className="px-4 py-4 border-t border-gray-200 bg-white rounded-b-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                    {/* Programa Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">Programa</Label>
                      <select
                        value={filterProgramId || ''}
                        onChange={(e) => setFilterProgramId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Todos</option>
                        {programs.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Period Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">Período</Label>
                      <select
                        value={filterPeriodId || ''}
                        onChange={(e) => setFilterPeriodId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Todos</option>
                        {periods.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Course Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">Materia</Label>
                      <select
                        value={filterCourseId || ''}
                        onChange={(e) => setFilterCourseId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Todas</option>
                        {courses.map(c => (
                          <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Classroom Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">Aula</Label>
                      <select
                        value={filterClassroomId || ''}
                        onChange={(e) => setFilterClassroomId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Todas</option>
                        {classrooms.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Professor Filter */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">Profesor</Label>
                      <select
                        value={filterProfessorId || ''}
                        onChange={(e) => setFilterProfessorId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Todos</option>
                        {professors.map(p => (
                          <option key={p.id} value={p.id}>{p.fullName}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      onClick={resetFilters}
                      variant="outline"
                      size="sm"
                      className="text-gray-700"
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <p className="text-gray-500 text-center py-8">Cargando calendario...</p>
            ) : calendarEvents.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No hay horarios programados</p>
                <Button onClick={openNewSchedule} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Primer Horario
                </Button>
              </div>
            ) : (
              <div className="bg-white">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView={calendarView}
                  initialDate={calendarDate || undefined}
                  datesSet={(arg) => {
                    setCalendarDate(arg.view.calendar.getDate());
                    setCalendarView(arg.view.type);
                  }}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay',
                  }}
                  locale="es"
                  slotMinTime="06:00:00"
                  slotMaxTime="22:00:00"
                  allDaySlot={false}
                  editable={true}
                  droppable={true}
                  events={calendarEvents}
                  eventContent={(eventInfo) => {
                    const { event } = eventInfo;
                    const props = event.extendedProps;
                    const formatDate = (dateStr: string) => {
                      if (!dateStr) return '';
                      try {
                        const date = new Date(dateStr + 'T00:00:00Z');
                        return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                      } catch {
                        return dateStr;
                      }
                    };
                    return (
                      <div className="p-2 overflow-hidden text-xs">
                        {props.periodCode && (
                          <div className="font-bold truncate text-[11px] uppercase tracking-wide">{props.periodCode}</div>
                        )}
                        <div className="font-semibold truncate">{props.courseName || event.title}</div>
                        <div className="truncate">🏫 {props.classroom || 'Sin aula'}</div>
                        <div className="truncate">👨‍🏫 {props.professor || 'Sin profesor'}</div>
                        {(props.startDate || props.endDate) && (
                          <div className="truncate text-gray-600 mt-1">
                            📅 {formatDate(props.startDate)} - {formatDate(props.endDate)}
                          </div>
                        )}
                        {props.sessionNumber && props.totalSessions && (
                          <div className="mt-1 inline-block bg-white/20 rounded px-1.5 py-0.5 text-[10px] font-medium">
                            Clase {props.sessionNumber} de {props.totalSessions}
                          </div>
                        )}
                        {(props.needsRescheduling || !props.classroomId || !props.professorId) && (
                          <div className="mt-1 inline-block bg-red-600 text-white rounded px-1.5 py-0.5 text-[10px] font-bold">
                            Reprogramar
                          </div>
                        )}
                      </div>
                    );
                  }}
                  eventDrop={async (info) => {
                    const event = info.event;
                    const oldEvent = info.oldEvent;
                    const props = event.extendedProps;
                    
                    const dayMapping: { [key: number]: string } = {
                      0: 'domingo',
                      1: 'lunes',
                      2: 'martes',
                      3: 'miércoles',
                      4: 'jueves',
                      5: 'viernes',
                      6: 'sábado',
                    };
                    
                    const newDay = dayMapping[event.start!.getDay()];
                    const horaInicio = event.start!.toTimeString().slice(0, 5);
                    const horaFin = event.end!.toTimeString().slice(0, 5);
                    const newDateStr = event.start!.getFullYear() + '-' +
                      String(event.start!.getMonth() + 1).padStart(2, '0') + '-' +
                      String(event.start!.getDate()).padStart(2, '0');

                    // GQB-05: arrastrar una clase RECURRENTE (sin fecha propia — se repite
                    // indefinidamente el mismo día de la semana) a otra fecha convierte esa
                    // fila en una sola sesión ahí, y las demás semanas de esa clase dejan de
                    // aparecer en el calendario. Confirmar antes en vez de perderlas en
                    // silencio (este era justo el bug reportado, visto al reproducirlo).
                    if (!props.startDate) {
                      const confirmed = window.confirm(
                        'Esta clase se repite cada semana (no tiene una fecha fija). Si la mueves así, ' +
                        'esta fila pasará a ser una sola sesión en la nueva fecha y las demás semanas de ' +
                        'esta clase ya no aparecerán en el calendario.\n\n¿Quieres continuar de todas formas?'
                      );
                      if (!confirmed) {
                        info.revert();
                        return;
                      }
                    }

                    try {
                      setLoading(true);
                      await updateSchedule(parseInt(event.id), {
                        courseId: props.courseId,
                        programId: props.programId,
                        periodId: props.periodId,
                        dia: newDay,
                        horaInicio,
                        horaFin,
                        classroomId: props.classroomId,
                        professorId: props.professorId,
                        startDate: newDateStr,
                        endDate: newDateStr,
                      });
                      
                      toast({
                        title: 'Horario actualizado',
                        description: 'El horario se movió correctamente',
                      });
                      
                      await refreshCalendar();
                    } catch (error: any) {
                      info.revert();
                      
                      const errorData = error.response?.data || {};
                      const conflicts = errorData.conflicts || [];
                      
                      let errorMessage = error.message || 'No se pudo mover el horario';
                      if (conflicts.length > 0) {
                        errorMessage = 'Conflictos detectados:\n' + conflicts.map((c: string) => `• ${c}`).join('\n');
                      }
                      
                      toast({
                        variant: 'destructive',
                        title: 'Error al mover horario',
                        description: errorMessage,
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  eventResize={async (info) => {
                    const event = info.event;
                    const props = event.extendedProps;
                    
                    const dayMapping: { [key: number]: string } = {
                      0: 'domingo',
                      1: 'lunes',
                      2: 'martes',
                      3: 'miércoles',
                      4: 'jueves',
                      5: 'viernes',
                      6: 'sábado',
                    };
                    
                    const newDay = dayMapping[event.start!.getDay()];
                    const horaInicio = event.start!.toTimeString().slice(0, 5);
                    const horaFin = event.end!.toTimeString().slice(0, 5);
                    const resizeDateStr = event.start!.getFullYear() + '-' +
                      String(event.start!.getMonth() + 1).padStart(2, '0') + '-' +
                      String(event.start!.getDate()).padStart(2, '0');

                    // GQB-05: mismo riesgo que en eventDrop — redimensionar una clase
                    // recurrente (sin fecha propia) también le fijaría la fecha de hoy y
                    // borraría las demás semanas en silencio si no se confirma primero.
                    if (!props.startDate) {
                      const confirmed = window.confirm(
                        'Esta clase se repite cada semana (no tiene una fecha fija). Si cambias su duración ' +
                        'así, esta fila pasará a ser una sola sesión en esta fecha y las demás semanas de ' +
                        'esta clase ya no aparecerán en el calendario.\n\n¿Quieres continuar de todas formas?'
                      );
                      if (!confirmed) {
                        info.revert();
                        return;
                      }
                    }

                    try {
                      setLoading(true);
                      await updateSchedule(parseInt(event.id), {
                        courseId: props.courseId,
                        programId: props.programId,
                        periodId: props.periodId,
                        dia: newDay,
                        horaInicio,
                        horaFin,
                        classroomId: props.classroomId,
                        professorId: props.professorId,
                        startDate: resizeDateStr,
                        endDate: resizeDateStr,
                      });
                      
                      toast({
                        title: 'Horario actualizado',
                        description: 'La duración se actualizó correctamente',
                      });
                      
                      await refreshCalendar();
                    } catch (error: any) {
                      info.revert();
                      
                      const errorData = error.response?.data || {};
                      const conflicts = errorData.conflicts || [];
                      
                      let errorMessage = error.message || 'No se pudo redimensionar el horario';
                      if (conflicts.length > 0) {
                        errorMessage = 'Conflictos detectados:\n' + conflicts.map((c: string) => `• ${c}`).join('\n');
                      }
                      
                      toast({
                        variant: 'destructive',
                        title: 'Error al redimensionar horario',
                        description: errorMessage,
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  eventClick={(info) => {
                    const event = info.event;
                    const props = event.extendedProps;
                    const dayMapping: { [key: number]: string } = {
                      0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miércoles',
                      4: 'Jueves', 5: 'Viernes', 6: 'Sábado',
                    };
                    setInfoCardEvent({
                      id: event.id,
                      title: event.title,
                      classroom: props.classroom || 'Sin asignar',
                      professor: props.professor || 'Sin asignar',
                      program: props.program || 'Sin programa',
                      period: props.period || 'Sin periodo',
                      periodCode: props.periodCode || '',
                      day: event.start ? dayMapping[event.start.getDay()] || props.day || '' : props.day || '',
                      startTime: event.start ? event.start.toTimeString().slice(0, 5) : props.startTime || '',
                      endTime: event.end ? event.end.toTimeString().slice(0, 5) : props.endTime || '',
                      startDate: props.startDate || '',
                      endDate: props.endDate || '',
                      courseId: props.courseId,
                      programId: props.programId,
                      periodId: props.periodId,
                      classroomId: props.classroomId,
                      classroomStatus: props.classroomStatus || null,
                      professorId: props.professorId,
                      sessionNumber: props.sessionNumber || null,
                      totalSessions: props.totalSessions || null,
                      needsRescheduling: props.needsRescheduling || false,
                    });
                  }}
                  height="auto"
                  buttonText={{
                    today: 'Hoy',
                    month: 'Mes',
                    week: 'Semana',
                    day: 'Día',
                  }}
                  weekends={true}
                  // GQB-06: agregar el número de día al header solo en semana/día — en la
                  // vista de mes el header es compartido por todas las semanas (mostraría un
                  // número fijo e incorrecto), y cada celda del cuerpo ya trae su propio
                  // número de día por separado (dayCellContent, sin tocar aquí).
                  dayHeaderFormat={calendarView === 'dayGridMonth' ? { weekday: 'long' } : { weekday: 'long', day: 'numeric' }}
                />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!infoCardEvent} onOpenChange={(open) => { if (!open) setInfoCardEvent(null); }}>
        <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold leading-tight">{infoCardEvent?.title}</h3>
                <p className="text-blue-100 text-sm mt-1">{infoCardEvent?.day} {infoCardEvent?.startTime} - {infoCardEvent?.endTime}</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {(() => {
              if (!infoCardEvent) return null;
              const reasons: string[] = [];
              if (!infoCardEvent.classroomId) reasons.push('No hay aula asignada');
              if (!infoCardEvent.professorId) reasons.push('No hay maestro asignado');
              if (infoCardEvent.needsRescheduling) {
                if (infoCardEvent.classroomStatus === 'mantenimiento') {
                  reasons.push('El aula asignada está en mantenimiento');
                } else {
                  reasons.push('Esta sesión cae en un día festivo');
                }
              }
              if (reasons.length === 0) return null;
              return (
                <div className="bg-red-50 border border-red-300 rounded-lg px-4 py-2.5 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded">Reprogramar</span>
                    <span className="text-xs font-semibold text-red-700">Esta sesión requiere atención</span>
                  </div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {reasons.map((r, i) => (
                      <li key={i} className="text-sm text-red-700">{r}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            {infoCardEvent?.sessionNumber && infoCardEvent?.totalSessions && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
                <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-bold text-blue-800">
                  Clase {infoCardEvent.sessionNumber} de {infoCardEvent.totalSessions} clases
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2.5 bg-gray-50 rounded-lg p-3">
                <GraduationCap className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Programa Académico</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{infoCardEvent?.program}</p>
                </div>
              </div>
              {infoCardEvent?.periodCode && (
                <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <Tag className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-blue-500 font-medium">Clave del Grupo</p>
                    <p className="text-sm font-bold text-blue-800 break-words">{infoCardEvent.periodCode}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2.5 bg-gray-50 rounded-lg p-3">
                <Calendar className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Grupo</p>
                  <p className="text-sm font-semibold text-gray-900 break-words">{infoCardEvent?.period}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 bg-gray-50 rounded-lg p-3">
                <MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Aula</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{infoCardEvent?.classroom}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 bg-gray-50 rounded-lg p-3">
                <User className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Maestro</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{infoCardEvent?.professor}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 bg-gray-50 rounded-lg p-3">
                <BookOpen className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Materia</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{infoCardEvent?.title.split(' - ').slice(1).join(' - ') || infoCardEvent?.title}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 bg-gray-50 rounded-lg p-3">
                <Clock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium">Fecha</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {infoCardEvent?.startDate ? new Date(infoCardEvent.startDate + 'T00:00:00Z').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Recurrente'}
                    {infoCardEvent?.endDate && infoCardEvent.endDate !== infoCardEvent.startDate && (
                      <> - {new Date(infoCardEvent.endDate + 'T00:00:00Z').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t px-5 py-3 flex justify-end gap-2 bg-gray-50">
            <Button variant="outline" size="sm" onClick={() => setInfoCardEvent(null)}>
              Cerrar
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (infoCardEvent) {
                  setInfoCardEvent(null);
                  openEditSchedule(parseInt(infoCardEvent.id), infoCardEvent);
                }
              }}
            >
              <Edit className="w-4 h-4 mr-1" />
              Editar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingSchedule ? 'Editar Horario' : 'Nuevo Horario de Clase'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editingSchedule ? handleUpdateSchedule : handleCreateSchedule}>
            <div className="space-y-4">
              {!editingSchedule && (
                <div>
                  <Label htmlFor="programId">Programa Académico *</Label>
                  <select
                    id="programId"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={scheduleFormData.programId}
                    onChange={(e) => {
                      const programId = parseInt(e.target.value);
                      setScheduleFormData({ 
                        ...scheduleFormData, 
                        programId,
                        courseId: 0,
                        classroomId: 0,
                        professorId: 0,
                      });
                      // Buscar el período que tiene este programa
                      const periodWithProgram = periods.find(p => p.programId === programId);
                      if (periodWithProgram) {
                        setSelectedPeriod(periodWithProgram);
                      }
                    }}
                    required
                  >
                    <option value="0">Seleccionar programa</option>
                    {programs.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label htmlFor="periodId">Período Académico</Label>
                <select
                  id="periodId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={scheduleFormData.periodId}
                  onChange={(e) =>
                    setScheduleFormData({ ...scheduleFormData, periodId: parseInt(e.target.value) || 0 })
                  }
                >
                  <option value="0">Sin período asignado</option>
                  {periods.map(period => (
                    <option key={period.id} value={period.id}>
                      {period.name} ({period.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="courseId">Materia del Programa *</Label>
                <select
                  id="courseId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={scheduleFormData.courseId}
                  onChange={(e) =>
                    setScheduleFormData({ 
                      ...scheduleFormData, 
                      courseId: parseInt(e.target.value),
                      classroomId: 0,
                      professorId: 0,
                    })
                  }
                  required
                  disabled={scheduleFormData.programId === 0}
                >
                  <option value="0">
                    {scheduleFormData.programId === 0 ? 'Selecciona programa primero' : 'Seleccionar materia'}
                  </option>
                  {programCourses.map(course => (
                    <option key={course.courseId} value={course.courseId}>
                      {course.courseCode} - {course.courseName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="dia">Día *</Label>
                {getAvailableDays().length > 0 ? (
                  <select
                    id="dia"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={scheduleFormData.dia}
                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, dia: e.target.value })}
                    required
                  >
                    {getAvailableDays().map(dia => (
                      <option key={dia.value} value={dia.value}>
                        {dia.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full px-3 py-2 border border-red-300 rounded-md bg-red-50 text-red-700 text-sm">
                    ⚠️ El período no tiene días configurados
                  </div>
                )}
              </div>

              {editingSchedule && (
                <div>
                  <Label htmlFor="fechaEspecifica">Fecha específica de esta clase</Label>
                  <Input
                    id="fechaEspecifica"
                    type="date"
                    value={scheduleFormData.fechaEspecifica}
                    onChange={(e) =>
                      setScheduleFormData({ ...scheduleFormData, fechaEspecifica: e.target.value })
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {editingSchedule.startDate
                      ? 'Cámbiala para reprogramar esta sesión a otro día, sin duplicarla.'
                      : 'Esta clase es recurrente (se repite cada semana). Ponle una fecha solo si quieres convertirla en una sesión única — las demás semanas dejarán de aparecer.'}
                  </p>
                </div>
              )}

              {editingSchedule?.startDate && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs text-amber-800 mb-2">
                    ¿Esta clase no se puede dar? Recórrela junto con todas las siguientes de la materia.
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={handleShiftSeries}>
                    Recorrer esta y las siguientes
                  </Button>
                </div>
              )}

              {getPermittedTimeRange() && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-xs text-blue-700 font-medium">
                    ⏰ Horario Permitido para este Grupo
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    {getPermittedTimeRange()?.startTime} - {getPermittedTimeRange()?.endTime}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="horaInicio">Hora Inicio *</Label>
                  <Input
                    id="horaInicio"
                    type="time"
                    value={scheduleFormData.horaInicio}
                    onChange={(e) =>
                      setScheduleFormData({ ...scheduleFormData, horaInicio: e.target.value })
                    }
                    required
                    min={getPermittedTimeRange()?.startTime}
                    max={getPermittedTimeRange()?.endTime}
                  />
                </div>

                <div>
                  <Label htmlFor="horaFin">Hora Fin *</Label>
                  <Input
                    id="horaFin"
                    type="time"
                    value={scheduleFormData.horaFin}
                    onChange={(e) =>
                      setScheduleFormData({ ...scheduleFormData, horaFin: e.target.value })
                    }
                    required
                    min={getPermittedTimeRange()?.startTime}
                    max={getPermittedTimeRange()?.endTime}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="classroomId">Aula</Label>
                <select
                  id="classroomId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={scheduleFormData.classroomId}
                  onChange={(e) =>
                    setScheduleFormData({ ...scheduleFormData, classroomId: parseInt(e.target.value) })
                  }
                  disabled={scheduleFormData.courseId === 0}
                >
                  <option value="">
                    {scheduleFormData.courseId === 0 ? 'Selecciona materia primero' : 'Sin asignar'}
                  </option>
                  {(() => {
                    const selectedCourse = programCourses.find(c => c.courseId === scheduleFormData.courseId);
                    if (!selectedCourse) return null;
                    
                    // Filter classrooms that have all required equipment
                    const requiredEquipmentIds = new Set(selectedCourse.equipments.map(e => e.id));
                    const filteredClassrooms = selectedCourse.classrooms.filter(classroom =>
                      Array.from(requiredEquipmentIds).length === 0 || 
                      classroom.id // Just show classrooms from the course data
                    );
                    
                    return filteredClassrooms.map(classroom => (
                      <option key={classroom.id} value={classroom.id}>
                        {classroom.name} (Capacidad: {classroom.capacity})
                      </option>
                    ));
                  })()}
                </select>
              </div>

              <div>
                <Label htmlFor="professorId">Profesor</Label>
                <select
                  id="professorId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={scheduleFormData.professorId}
                  onChange={(e) =>
                    setScheduleFormData({ ...scheduleFormData, professorId: parseInt(e.target.value) })
                  }
                  disabled={scheduleFormData.courseId === 0}
                >
                  <option value="">
                    {scheduleFormData.courseId === 0 ? 'Selecciona materia primero' : 'Sin asignar'}
                  </option>
                  {(() => {
                    const selectedCourse = programCourses.find(c => c.courseId === scheduleFormData.courseId);
                    return selectedCourse?.teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName}
                      </option>
                    )) || null;
                  })()}
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center gap-3 mt-6">
              <div>
                {editingSchedule && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      if (confirm('¿Estás seguro de que deseas eliminar este horario?')) {
                        setIsScheduleDialogOpen(false);
                        resetScheduleForm();
                        handleDeleteSchedule(editingSchedule.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsScheduleDialogOpen(false);
                    resetScheduleForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit">{editingSchedule ? 'Actualizar' : 'Crear'}</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Strategy Dialog */}
      <Dialog open={isStrategyDialogOpen} onOpenChange={setIsStrategyDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Horarios Existentes Detectados</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
              Se encontraron <strong>{conflictCount} horarios</strong> previamente generados para este período.
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="replace"
                  name="strategy"
                  value="replace"
                  checked={selectedStrategy === 'replace'}
                  onChange={(e) => setSelectedStrategy(e.target.value as 'replace' | 'append')}
                  className="w-4 h-4"
                />
                <div>
                  <label htmlFor="replace" className="font-semibold cursor-pointer">Reemplazar Existentes</label>
                  <p className="text-sm text-gray-600">Elimina y regenera las sesiones futuras. Las sesiones que ya tienen asistencia tomada se conservan tal cual, con su historial intacto.</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="append"
                  name="strategy"
                  value="append"
                  checked={selectedStrategy === 'append'}
                  onChange={(e) => setSelectedStrategy(e.target.value as 'replace' | 'append')}
                  className="w-4 h-4"
                />
                <div>
                  <label htmlFor="append" className="font-semibold cursor-pointer">Acoplar (Intercalar)</label>
                  <p className="text-sm text-gray-600">Mantiene horarios existentes e intercala nuevos en espacios disponibles</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsStrategyDialogOpen(false)}
              disabled={isGenerating}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleGeneratePreview(selectedStrategy)}
              disabled={isGenerating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                'Continuar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Schedules Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Eliminar Horarios del Período</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              ¿Estás seguro de que deseas eliminar todos los horarios del período <strong>{selectedPeriod?.code} - {selectedPeriod?.name}</strong>?
            </p>
            <p className="text-red-600 text-sm mt-2">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePeriodSchedules}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Horarios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Schedules Dialog */}
      <Dialog open={isGenerateDialogOpen} onOpenChange={(open) => {
        setIsGenerateDialogOpen(open);
        if (!open) {
          setGenerateStep('info');
          setGeneratePreview([]);
          setGenerateWarnings([]);
          setGenerateError('');
          setGenerateSuccess('');
        }
      }}>
        <DialogContent className={generateStep === 'preview' ? 'sm:max-w-[800px] max-h-[85vh] overflow-y-auto' : 'sm:max-w-[500px]'}>
          <DialogHeader>
            <DialogTitle>Generar Horarios Automáticamente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {generateError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5 flex-shrink-0">⚠️</span>
                  <p className="text-red-700 text-sm font-medium whitespace-pre-wrap">{generateError}</p>
                </div>
                {generateError.toLowerCase().includes('materias') && (
                  <div className="ml-6 text-xs text-red-600 bg-red-100 rounded p-2">
                    <strong>¿Cómo solucionarlo?</strong> Ve a <em>Control Escolar → Grupos</em>, selecciona este período y asigna las materias a los días de la semana en la pestaña de configuración de horario.
                  </div>
                )}
                {generateError.toLowerCase().includes('fecha de inicio') && (
                  <div className="ml-6 text-xs text-red-600 bg-red-100 rounded p-2">
                    <strong>¿Cómo solucionarlo?</strong> Ve a <em>Control Escolar → Grupos</em>, edita este período y configura la Fecha de Inicio.
                  </div>
                )}
              </div>
            )}
            {generateSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded whitespace-pre-wrap text-sm">
                {generateSuccess}
              </div>
            )}

            {generateStep === 'info' && (
              <>
                <div>
                  <Label>Período Seleccionado:</Label>
                  <p className="text-gray-700 font-medium mt-2">
                    {selectedPeriod?.code} - {selectedPeriod?.name}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm text-blue-700">
                    <strong>ℹ️ Algoritmo de Generación:</strong> El sistema genera clases respetando:
                  </p>
                  <ul className="text-sm text-blue-600 mt-2 list-disc list-inside space-y-1">
                    <li>Número de semanas calculado dinámicamente según total_sessions de cada materia</li>
                    <li>Clases distribuidas en los días definidos del período (Lunes, Miércoles, Viernes)</li>
                    <li>Cantidad exacta de clases por materia según total_sessions</li>
                    <li>Duración exacta de cada clase definida por materia (hoursPerSession)</li>
                    <li>Docentes priorizados por contrato fijo (tipoContrato = 'fijo')</li>
                    <li>Aulas asignadas según equipamiento requerido por materia</li>
                    <li>Feriados institucionales (se saltan automáticamente)</li>
                    <li>Instancias individuales de clases para cada día y semana</li>
                    <li>Fechas de inicio y fin calculadas considerando solo días de clase</li>
                    {formatPeriodSchedule().length > 0 && (
                      <>
                        <li className="font-medium mt-2 list-none">
                          Horarios definidos en el grupo:
                        </li>
                        {formatPeriodSchedule().map((schedule, idx) => (
                          <li key={idx} className="ml-4 list-none">
                            • {schedule}
                          </li>
                        ))}
                      </>
                    )}
                  </ul>
                </div>
              </>
            )}

            {generateStep === 'preview' && (
              <>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      generatePreview.length === generateExpectedTotal ? 'bg-green-100 text-green-700' :
                      generatePreview.length < generateExpectedTotal ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {generatePreview.length} / {generateExpectedTotal} clases
                    </span>
                    {generatePreview.length !== generateExpectedTotal && (
                      <span className="text-sm text-gray-500">
                        ({generatePreview.length < generateExpectedTotal 
                          ? `Faltan ${generateExpectedTotal - generatePreview.length}` 
                          : `${generatePreview.length - generateExpectedTotal} extra`})
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyPreviewToClipboard(generatePreview)}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPreviewCSV(generatePreview, `horarios-${selectedPeriod?.code || 'export'}.csv`)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>

                {generateWarnings.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    {generateWarnings.map((w, i) => (
                      <p key={i} className="text-sm text-yellow-700 whitespace-pre-wrap">{w}</p>
                    ))}
                  </div>
                )}

                <div className="border rounded-md overflow-hidden max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Clase</th>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Día</th>
                        <th className="px-3 py-2 text-left">Horario</th>
                        <th className="px-3 py-2 text-left">Materia</th>
                        <th className="px-3 py-2 text-left">Profesor ✏️</th>
                        <th className="px-3 py-2 text-left">Aula ✏️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatePreview.map((s: any, i: number) => {
                        const courseData = programCourses.find(pc => pc.courseId === s.materiaId);
                        const availableTeachers = courseData?.teachers || [];
                        
                        return (
                          <tr key={i} className={s.needsRescheduling ? 'bg-red-50' : (i % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                            <td className="px-3 py-2 whitespace-nowrap text-blue-700 font-medium">
                              {s.sessionNumber && s.totalSessions ? `${s.sessionNumber} de ${s.totalSessions}` : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span>{s.fecha}</span>
                                {s.needsRescheduling && (
                                  <span className="inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                    Reprogramar
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 capitalize">{s.dia}</td>
                            <td className="px-3 py-2">{s.horaInicio}-{s.horaFin}</td>
                            <td className="px-3 py-2">{s.materia}</td>
                            <td className="px-3 py-2">
                              {availableTeachers.length > 1 ? (
                                <select
                                  value={s.profesorId || ''}
                                  onChange={(e) => {
                                    const newTeacherId = parseInt(e.target.value);
                                    const newTeacher = availableTeachers.find(t => t.id === newTeacherId);
                                    if (newTeacher) {
                                      setGeneratePreview(prev => prev.map((item) => 
                                        item.materiaId === s.materiaId
                                          ? { ...item, profesorId: newTeacherId, profesor: newTeacher.fullName }
                                          : item
                                      ));
                                    }
                                  }}
                                  className="w-full px-2 py-1 text-sm border rounded bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                >
                                  {availableTeachers.map(teacher => (
                                    <option key={teacher.id} value={teacher.id}>
                                      {teacher.fullName}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span>{s.profesor}</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {(courseData?.classrooms || []).length > 0 ? (
                                <select
                                  value={s.aulaId || ''}
                                  onChange={(e) => {
                                    const newClassroomId = parseInt(e.target.value);
                                    const allClassrooms = courseData?.classrooms || [];
                                    const newClassroom = allClassrooms.find(c => c.id === newClassroomId);
                                    if (newClassroom) {
                                      setGeneratePreview(prev => prev.map((item) => 
                                        item.materiaId === s.materiaId
                                          ? { ...item, aulaId: newClassroomId, aula: newClassroom.name, classroomId: newClassroomId }
                                          : item
                                      ));
                                    }
                                  }}
                                  className="w-full px-2 py-1 text-sm border rounded bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                >
                                  {classrooms.map(classroom => (
                                    <option key={classroom.id} value={classroom.id}>
                                      {classroom.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span>{s.aula}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            {generateStep === 'info' ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsGenerateDialogOpen(false)}
                  disabled={isGenerating}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={checkForConflicts}
                  disabled={isGenerating}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    'Generar Vista Previa'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setGenerateStep('info')}
                  disabled={isGenerating}
                >
                  Volver
                </Button>
                <Button
                  onClick={handleApplyPreview}
                  disabled={isGenerating || generatePreview.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Aplicando...
                    </>
                  ) : (
                    `Aplicar ${generatePreview.length} Horarios`
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog open={isAIDialogOpen} onOpenChange={setIsAIDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Generador de Horarios con IA
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {aiError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {aiError}
              </div>
            )}

            {selectedPeriod && (
              <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                <p className="text-sm text-purple-800">
                  <strong>Grupo:</strong> {selectedPeriod.code} - {selectedPeriod.name}
                </p>
              </div>
            )}

            {aiStep === 'rules' && (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center">
                    <Label>Reglas de Generacion (personalizables):</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await fetch('/api/schedule-rules', {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ rules: aiRules }),
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setAIRules(data.rules);
                            toast({ title: 'Reglas guardadas', description: 'Las reglas se guardaron correctamente' });
                          }
                        } catch (error) {
                          console.error('Error saving rules:', error);
                          toast({ variant: 'destructive', title: 'Error', description: 'No se pudieron guardar las reglas' });
                        }
                      }}
                      disabled={aiLoading}
                    >
                      Guardar Reglas
                    </Button>
                  </div>
                  <textarea
                    value={aiRules}
                    onChange={(e) => setAIRules(e.target.value)}
                    className="w-full h-48 mt-2 p-3 border border-gray-300 rounded-md font-mono text-sm"
                    placeholder="# Reglas de generacion de horarios..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puedes modificar las reglas para personalizar como la IA genera los horarios. Las reglas se guardan por institucion.
                  </p>
                </div>
              </div>
            )}

            {aiStep === 'generating' && (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 text-purple-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">{aiProgressMessage || 'La IA esta analizando y generando horarios...'}</p>
                <p className="text-sm text-gray-500 mt-2">Esto puede tomar unos segundos</p>
                <div className="mt-4 max-w-md mx-auto">
                  <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-600 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                </div>
              </div>
            )}

            {aiStep === 'refining' && (
              <div className="text-center py-12">
                <RefreshCw className="h-12 w-12 text-purple-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">{aiProgressMessage || 'La IA esta ajustando los horarios...'}</p>
                <div className="mt-4 max-w-md mx-auto">
                  <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-600 rounded-full animate-pulse" style={{ width: '40%' }}></div>
                  </div>
                </div>
              </div>
            )}

            {aiStep === 'preview' && (
              <div className="space-y-4">
                {aiExplanation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">{aiExplanation}</p>
                  </div>
                )}

                {aiWarnings.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-600 text-lg">⚠️</span>
                      <div className="flex-1">
                        <p className="font-medium text-yellow-800 mb-1">Advertencias de generación</p>
                        {aiWarnings.map((warning, idx) => (
                          <p key={idx} className="text-sm text-yellow-700">{warning}</p>
                        ))}
                        <p className="text-xs text-yellow-600 mt-2">
                          Puede solicitar ajustes abajo o regenerar para intentar obtener las clases faltantes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="max-h-64 overflow-y-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Materia</th>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Horario</th>
                        <th className="px-3 py-2 text-left">Profesor</th>
                        <th className="px-3 py-2 text-left">Aula</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {aiPreviewSchedules.map((schedule, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2">{schedule.materia}</td>
                          <td className="px-3 py-2">{schedule.fecha}</td>
                          <td className="px-3 py-2">{schedule.horaInicio} - {schedule.horaFin}</td>
                          <td className="px-3 py-2">{schedule.profesor}</td>
                          <td className="px-3 py-2">{schedule.aula}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      aiPreviewSchedules.length === aiExpectedTotal 
                        ? 'bg-green-100 text-green-800' 
                        : aiPreviewSchedules.length < aiExpectedTotal 
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                    }`}>
                      {aiPreviewSchedules.length} / {aiExpectedTotal} clases
                    </span>
                    {aiPreviewSchedules.length < aiExpectedTotal && (
                      <span className="text-xs text-yellow-600">
                        (faltan {aiExpectedTotal - aiPreviewSchedules.length})
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const text = aiPreviewSchedules.map((s, i) => 
                          `${i+1}. ${s.materia} | ${s.fecha} | ${s.horaInicio}-${s.horaFin} | ${s.profesor} | ${s.aula}`
                        ).join('\n');
                        navigator.clipboard.writeText(text);
                        toast({ title: 'Copiado', description: 'Listado copiado al portapapeles' });
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const headers = 'No,Materia,Fecha,Hora Inicio,Hora Fin,Profesor,Aula';
                        const rows = aiPreviewSchedules.map((s, i) => 
                          `${i+1},"${s.materia}","${s.fecha}","${s.horaInicio}","${s.horaFin}","${s.profesor}","${s.aula}"`
                        );
                        const csv = [headers, ...rows].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `clases_programadas_${new Date().toISOString().split('T')[0]}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label>Solicitar ajustes (opcional):</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={aiFeedback}
                      onChange={(e) => setAIFeedback(e.target.value)}
                      placeholder="Ej: Mover Matematicas al lunes por la manana..."
                      className="flex-1"
                    />
                    <Button
                      onClick={handleAIRefine}
                      disabled={!aiFeedback.trim() || aiLoading}
                      variant="outline"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label>Estrategia al aplicar:</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="aiStrategy"
                        value="replace"
                        checked={selectedStrategy === 'replace'}
                        onChange={() => setSelectedStrategy('replace')}
                      />
                      <span className="text-sm">Reemplazar existentes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="aiStrategy"
                        value="append"
                        checked={selectedStrategy === 'append'}
                        onChange={() => setSelectedStrategy('append')}
                      />
                      <span className="text-sm">Agregar a existentes</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsAIDialogOpen(false)}
              disabled={aiLoading}
            >
              Cancelar
            </Button>
            
            {aiStep === 'rules' && (
              <Button
                onClick={handleAIGenerate}
                disabled={aiLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Generar Vista Previa
              </Button>
            )}
            
            {aiStep === 'preview' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setAIStep('rules')}
                  disabled={aiLoading}
                >
                  Volver a Reglas
                </Button>
                <Button
                  onClick={handleAIApply}
                  disabled={aiLoading || aiPreviewSchedules.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Aplicando...
                    </>
                  ) : (
                    <>Aplicar {aiPreviewSchedules.length} Clases</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
